import {
  Chart,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
} from '@progress/kendo-react-charts';
import { produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { formatPrice } from '../../../../../common/global/helpers';
import Dropdown from '../../../../../common/widgets/Dropdown';
import Loader from '../../../../../common/widgets/Loader';
import { config } from '../../../config';
import { DropdownOptionModel } from '../../../models/Common';
import {
  CategoryStatisticModel,
  OrdersStatisticsModel,
} from '../../../models/Orders';
import { resources } from '../../../resources/resources';
import { orderService } from '../../../services/orders';
import { EmptyStateDashboardMyExpenses } from '../../Common/EmptyStates';

interface FiltersData {
  period: DropdownOptionModel;
}

interface OrdersStatisticsPageState {
  isLoading: boolean;
  model?: OrdersStatisticsModel;
  filter: FiltersData;
}

export const OrdersStatistics = () => {
  const { t: translate } = useTranslation();

  const periodOptions: DropdownOptionModel[] = useMemo(
    () => [
      {
        text: translate(resources.fromLastNDays, { 0: 30 }),
        value: new Date(moment().add({ days: -30 }).toString()),
      },
      {
        text: translate(resources.fromLastNDays, { 0: 90 }),
        value: new Date(moment().add({ days: -90 }).toString()),
      },
      {
        text: translate(resources.fromLastNMonths, { 0: 6 }),
        value: new Date(moment().add({ months: -6 }).toString()),
      },
      {
        text: translate(resources.fromLastYear),
        value: new Date(moment().add({ years: -1 }).toString()),
      },
      { text: translate(resources.all), value: null },
    ],
    [translate],
  );

  const [state, setState] = useState<OrdersStatisticsPageState>({
    isLoading: true,
    filter: {
      period: periodOptions[0],
    },
  });
  useEffect(() => {
    const controller = new AbortController();

    const loadOrdersStatistics = async (newerThanDate?: Date) => {
      const response = await orderService.getOrdersStatistics(newerThanDate, {
        signal: controller?.signal,
      });

      if (controller.signal.aborted) return;

      if (!response.hasErrors) {
        setState(
          produce(draft => {
            draft.model = {
              totalAmount: response.data.totalAmount,
              statistics: response.data.statistics,
            };
          }),
        );
      }

      setState(
        produce(draft => {
          draft.isLoading = false;
        }),
      );
    };

    loadOrdersStatistics(state.filter.period.value);

    return () => {
      controller.abort();
    };
  }, [state.filter.period.value]);

  const dataMapper = (statistics: CategoryStatisticModel[]) => {
    const colors = [
      '#83D0D5',
      '#3AB29C',
      '#FCB655',
      '#FB6F43',
      '#FF220D',
      '#B31C49',
      '#FF417A',
      '#FC81AD',
      '#E295F7',
      '#B816D8',
      '#751886',
      '#7727FA',
      '#318BFB',
      '#71F4E5',
      '#81C105',
    ];

    return statistics.map((category, index) => ({
      kind: category.categoryName,
      share: category.amount,
      color: index < colors.length ? colors[index] : '',
    }));
  };

  return (
    <div className="panel padding-xl">
      <div className="mydash-panel-header mydash-panel-header-filters">
        <h2 className="mydash-title">
          {translate(resources.accountDashboard.expenseStatistics)}
        </h2>
        <div className="mydash-panel-filters">
          <Dropdown
            data={periodOptions}
            value={state.filter.period}
            onChange={e =>
              setState(
                produce(draft => {
                  draft.filter.period = e.value;
                }),
              )
            }
            dataItemKey="value"
            textField="text"
          />
        </div>
      </div>
      <Loader loading={state.isLoading} loader={<Skeleton />}>
        <div className="mydash-panel-content">
          <div className="order-statistics">
            {state.model && state.model?.totalAmount > 0 ? (
              <>
                <Chart
                  donutCenterRender={() =>
                    DonutCenterRenderer(state?.model?.totalAmount)
                  }
                >
                  <ChartSeries>
                    <ChartSeriesItem
                      holeSize={90}
                      type="donut"
                      data={dataMapper(state.model?.statistics)}
                      categoryField="kind"
                      field="share"
                      color={'red'}
                    ></ChartSeriesItem>
                  </ChartSeries>
                  <ChartLegend visible={false} />
                </Chart>
                <Legend
                  statistics={dataMapper(state.model.statistics)}
                  totalAmount={state.model.totalAmount}
                />
              </>
            ) : (
              <EmptyStateDashboardMyExpenses />
            )}
          </div>
        </div>
      </Loader>
    </div>
  );
};

const Legend = (props: {
  statistics: { kind: string; share: number; color: string }[];
  totalAmount: number;
}) => {
  if (_.isNil(props.statistics)) {
    return null;
  }

  return (
    <ul className="donut-legend">
      {props.statistics.map((item: any, index: number) => {
        return (
          <li key={index}>
            <span>
              <span
                className="legend-bullet"
                style={{ borderColor: item.color }}
              ></span>
              {_.unescape(item.kind)}
            </span>
            <span>{Math.round((item.share / props.totalAmount) * 100)}%</span>
            <span>
              {formatPrice(item.share)} <sup>{config.currency}</sup>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const DonutCenterRenderer = (totalAmount?: number) => {
  const { t: translate } = useTranslation();

  return (
    <span className="donut-center">
      <span>{translate(resources.statistics.spentAmount)}</span>
      <div>
        {formatPrice(totalAmount)} <sup>{config.currency}</sup>
      </div>
    </span>
  );
};
